import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, Space, Button, Spin, Progress, Popover } from 'antd';
import { ZoomInOutlined } from '@ant-design/icons';

import { columnSearchWidget, LoadingScreen, TextShortener } from 'components';
import ETFDetailsModal from './ETFDetailsModal';
import BondDetailsModal from './BondDetailsModal';
import PriceCell from './PriceCell';

import {
  AssetClassOptions,
  domicilesArray,
  FinancialInstrument,
  HISTORICAL_SCENARIO,
  issuerTypeArray,
  REAL_TIME_SCENARIO,
  ReusableInstrumentTableType,
  selectors,
  UnderlyingAsset,
  utils,
} from 'state';
import { formatters } from 'services';
import {
  assetIsOpenNew,
  forwardInstrumetnReportingValue,
  tBillInstrumentReportingValue,
} from '../state/app/utils';
import type { TableColumnType as ColumnType } from 'antd';
import { fwdContractsIndexed } from '../state/app/selectors';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { auctions_tenors } from '../state/app/auctions/types';

// Futures-only: how much of a leveraged position's margin cushion has been eaten by unrealized loss.
// Liquidation triggers when equity (margin + pnl) <= maintenanceThreshold * margin, i.e. once the
// unrealized loss reaches the loss budget = (1 - maintenanceThreshold) * margin. 0% = full cushion,
// 100% = at the liquidation point. Pure ratio of same-currency quantities (for futures the instrument
// currency is the reporting currency), so it is currency-agnostic.
const futuresMarginUsedPct = (
  margin: number,
  unrealizedPnl: number,
  maintenanceThreshold: number
): number => {
  if (!margin || margin <= 0) {
    return 0;
  }
  const lossBudget = (1 - maintenanceThreshold) * margin;
  const loss = Math.max(0, -unrealizedPnl);
  if (lossBudget <= 0) {
    // degenerate: threshold == 1 means any loss is an immediate margin call
    return loss > 0 ? 100 : 0;
  }
  return Math.min(100, Math.max(0, (loss / lossBudget) * 100));
};

// Green when there is plenty of cushion, amber as it tightens, red near the liquidation point.
const marginUsedColor = (pct: number): string =>
  pct >= 70 ? '#f5222d' : pct >= 30 ? '#faad14' : '#52c41a';

type props = {
  financialInstruments: FinancialInstrument[];
  selectedInstrumentId?: number;
  selectInstrument?: (instrument: FinancialInstrument) => void;
  action?: (text: string, record: any, index: number) => React.ReactNode;
  marketHours?: boolean;
  priceArrows?: boolean;
  playerId: number | undefined;
  trainer_action?: (
    text: string,
    record: any,
    index: number
  ) => React.ReactNode;
};

const InstrumentTable: React.FC<props> = ({
  financialInstruments,
  selectedInstrumentId,
  action,
  marketHours,
  priceArrows,
  playerId,
  trainer_action,
}) => {
  // console.log(`InstrumentTable from trainer ${playerId}`);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const player = useSelector(selectors.player);
  const currencies = useSelector(selectors.currencies);
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);
  const contractsIndexed = useSelector(selectors.fwdContractsIndexed(playerId));
  // console.log('contractsIndexed', contractsIndexed);
  const contractsFetching = useSelector(
    selectors.fwdContractsFetching(playerId)
  );
  const marketHoursNew = useSelector(selectors.marketHours);
  // console.log('marketHoursNew: ', marketHoursNew);

  const session = useSelector(selectors.activeSession);
  const reportingCurrency = utils.useReportingCurrency(session.id);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBondDetailsModal, setShowBondDetailsModal] = useState(false);
  const [instrument, setInstrument] = useState<FinancialInstrument>(
    financialInstruments[0]
  );
  const [detailsAssetId, setDetailsAssetId] = useState(0);

  const playableCurrencies = useSelector(
    selectors.playableCurrencies(financialInstruments)
  );

  const currencyFilters = playableCurrencies.map(symbol => {
    return {
      text: symbol,
      value: symbol,
    };
  });
  // console.log('instrument table');
  // console.log('contractsFetching: ', contractsFetching);
  // console.log(
  //   'resourceType: ',
  //   underlyingAssets[instrument.underlying_asset].resourcetype
  // );
  if (
    playerId === undefined &&
    underlyingAssets[instrument.underlying_asset].resourcetype ===
      'FxForwards' &&
    contractsFetching
  ) {
    // console.log('entered loading');
    return (
      <Spin indicator={<LoadingOutlined style={{ fontSize: 150 }} spin />} />
    );
  }
  if (
    playerId === undefined &&
    (!player?.id || Object.keys(underlyingAssets).length === 0)
  ) {
    // makes sure player.id is available before the assetsMap .filter()
    return <Spin />;
  }

  // const assetsToShow_tpl1: number[] = [1, 2];
  // const assetsToShow_tpl2: number[] = [3, 4];
  // const assetsToShow_tpl3: number[] = [5, 6];
  const assetsMap: Record<number, number[]> = {
    16445: [1387, 1702, 1732, 1195, 1211],
    16446: [1387, 1702, 1732, 1195, 1211],
    16447: [1387, 1702, 1732, 1195, 1211],
    16448: [1387, 1702, 1732, 1195, 1211],
    16449: [1387, 1702, 1732, 1195, 1211],
    16450: [1387, 1702, 1732, 1195, 1211],
    16451: [1387, 1702, 1732, 1195, 1211],
    16452: [1387, 1702, 1732, 1195, 1211],
    16453: [1387, 1702, 1732, 1195, 1211],
    16454: [1387, 1702, 1732, 1195, 1211],
    16455: [1387, 1702, 1732, 1195, 1211],
    16456: [1387, 1702, 1732, 1195, 1211],
    16457: [1387, 1702, 1732, 1195, 1211],
    16458: [1387, 1702, 1732, 1195, 1211],
    16459: [1387, 1702, 1732, 1195, 1211],
    // 2019: [5, 6],
    // re-deploy
  };
  const dataSource = financialInstruments
    .filter(item => {
      // lookup the allowed assets array for this player.id (or undefined)
      // console.log(player.id);
      const allowed = assetsMap[player.id];
      // if there is no entry in the map, include all items
      if (!allowed) return true;
      // otherwise only include if the underlying_asset is in the allowed list
      return !allowed.includes(item.underlying_asset);
    })
    .filter(item => {
      // hide certain assets from player instrument table
      return item.id !== 1355;
    })
    // .filter(item => {
    //   // console.log(playerId);
    //   if (player.id === 2017) {
    //     if (assetsToShow_tpl1.includes(item.underlying_asset)) {
    //       return true;
    //     } else {
    //       return false;
    //     }
    //   } else {
    //     return true;
    //   }
    // })
    // .filter(item => {
    //   if (player.id === 2018) {
    //     if (assetsToShow_tpl2.includes(item.underlying_asset)) {
    //       return true;
    //     } else {
    //       return false;
    //     }
    //   } else {
    //     return true;
    //   }
    // })
    // .filter(item => {
    //   if (player.id === 2019) {
    //     if (assetsToShow_tpl2.includes(item.underlying_asset)) {
    //       return true;
    //     } else {
    //       return false;
    //     }
    //   } else {
    //     return true;
    //   }
    // })
    .map(item => ({
      ...item,
      ...underlyingAssets[item.underlying_asset],
      market_history: undefined,
      id: item.id,
    }))
    .map(item => {
      // console.log('item.underlying_asset: ', item.underlying_asset);
      // console.log('underlyingAssets: ', underlyingAssets);
      const assetMarketHours =
        marketHoursNew[underlyingAssets[item.underlying_asset].market_hours];
      // console.log('assetMarketHours: ', assetMarketHours);
      const [marketIsOpen, closedFromInterval] = utils.assetIsOpenNew(
        item as any,
        session,
        assetMarketHours
      );
      const currency = currencies[item.base_currency];

      const returnPercent = item.avg_open_price
        ? ((item.bid - item.avg_open_price) / item.avg_open_price) *
          Math.sign(item.amount)
        : 0;
      let reportingValue = utils.reportingValue(
        item as any,
        currency,
        item.resourcetype,
        assetList,
        currencyList,
        reportingCurrency
      );
      if (
        underlyingAssets[item.underlying_asset].resourcetype === 'FxForwards'
      ) {
        // reportingValue = 0;
        reportingValue = utils.forwardInstrumetnReportingValue(
          item,
          underlyingAssets[item.underlying_asset],
          session,
          assetList,
          currencyList,
          contractsIndexed
        );
        if (item.amount === 0) reportingValue = 0;
      } else if (
        underlyingAssets[item.underlying_asset].resourcetype === 'TBill'
      ) {
        reportingValue = utils.tBillInstrumentReportingValue(
          item,
          underlyingAssets[item.underlying_asset],
          session,
          assetList,
          currencyList
        );
      }
      return {
        ...item,
        currency: currency.symbol,
        return_percent: returnPercent,
        marketIsOpen,
        closedFromInterval,
        reportingValue,
      };
    })
    .filter(item => item.bid);

  const isETF = dataSource[0]?.resourcetype.includes('ETF');

  const bondsCashOrder = ['US', 'DE', 'FR', 'IT', 'UK', 'GB', 'JP', 'AU'];

  if (dataSource[0]?.resourcetype.endsWith('Bond')) {
    dataSource.sort((a, b) => {
      const tickerA = a.ticker;
      const tickerB = b.ticker;

      const indexA = bondsCashOrder.indexOf(tickerA.slice(0, 2));
      const indexB = bondsCashOrder.indexOf(tickerB.slice(0, 2));

      if (indexA != indexB) return indexA - indexB;

      return parseInt(tickerA.slice(2, 4)) - parseInt(tickerB.slice(2, 4));
    });
  }

  const fxOrder = [
    'EURUSD',
    'USDJPY',
    'GBPUSD',
    'USDCHF',
    'AUDUSD',
    'NZDUSD',
    'USDTRY',
    'USDCNY',
    'USDSGD',
  ];

  if (dataSource[0]?.resourcetype === 'FX') {
    dataSource.sort(
      (a, b) =>
        fxOrder.indexOf(a.name?.split(' ')[0]) -
        fxOrder.indexOf(b.name?.split(' ')[0])
    );
  }

  const extractFilters = (dataIndex: string) =>
    dataSource
      .map((item: any) => item[dataIndex])
      .filter((v, i, a) => a.indexOf(v) === i)
      .map(g => ({ text: g, value: g }));

  let additionFields: any[] = [];
  let firstFields: any[] = [];
  if (dataSource[0]?.resourcetype === 'Equity') {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'Region',
        dataIndex: 'geography',
        key: 'geography',
        filters: extractFilters('geography'),
        onFilter: (value: any, record: any) => record.geography === value,
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
  }

  if (dataSource[0]?.resourcetype === 'Futures') {
    // Futures render like Equity (Name / Region / BBG Ticker); flat list in the divisor.
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'Region',
        dataIndex: 'geography',
        key: 'geography',
        filters: extractFilters('geography'),
        onFilter: (value: any, record: any) => record.geography === value,
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
  }

  if (
    dataSource[0]?.resourcetype === 'FX' ||
    dataSource[0]?.resourcetype === 'Metal'
  ) {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
  }

  if (dataSource[0]?.resourcetype === 'FxForwards') {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
      // {
      //   title: 'Open Date',
      //   dataIndex: 'open_date',
      //   key: 'open_date',
      //   // defaultSortOrder: 'descend',
      //   // sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      // },
      {
        title: 'Maturity Date',
        dataIndex: 'fwd_maturity_date',
        key: 'fwd_maturity_date',
        // defaultSortOrder: 'descend',
        // sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
    // console.log('action: ', action);
    if (trainer_action !== undefined) {
      // meaning if this table is opened in trainer view (I don't like this reusable component)
      // additionFields = [
      //   {
      //     title: 'Action',
      //     key: 'action',
      //   },
      // ];
      additionFields.push({
        title: 'Action',
        render: trainer_action,
        fixed: 'right',
      });
    }
  }

  if (dataSource[0]?.resourcetype === 'Crypto') {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
  }

  if (isETF) {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
        render: function Name(text: string, record: any) {
          return (
            <Space>
              <Button
                onClick={() => {
                  setShowDetailsModal(true);
                  setDetailsAssetId(record.underlying_asset);
                }}
              >
                <ZoomInOutlined />
              </Button>
              {TextShortener({ text, maxLength: 25 })}
            </Space>
          );
        },
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
      // {
      //   title: 'Classification',
      //   dataIndex: 'classification',
      //   filters: extractFilters('classification'),
      //   onFilter: (value: any, record: any) => record.geography === value,
      // },
    ];
  }

  // if (dataSource[0]?.resourcetype.endsWith('Bond')) {
  if (
    dataSource[0]?.resourcetype == 'Bond' ||
    dataSource[0]?.resourcetype == 'CorporateBond' ||
    dataSource[0]?.resourcetype == 'GovtBond'
  ) {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
        render(text: string, record: any) {
          return (
            <Space>
              {session.scenario_type === REAL_TIME_SCENARIO && (
                <Button
                  onClick={() => {
                    setDetailsAssetId(record.underlying_asset);
                    const selectedFin = financialInstruments.filter(fin => {
                      if (fin.id === record.id) return fin;
                    })?.[0];
                    setInstrument(selectedFin);
                    setShowBondDetailsModal(true);
                  }}
                >
                  <ZoomInOutlined />
                </Button>
              )}
              {TextShortener({ text, maxLength: 25 })}
            </Space>
          );
        },
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
    ];
    additionFields = [
      {
        title: 'Coupon Date',
        dataIndex: 'coupon_date',
        key: 'coupon_date',
        align: 'right',

        // sorter: (a: any, b: any) => a.coupon_date.localeCompare(b.coupon_date),
        // defaultSortOrder: 'descend',
      },
      // {
      //   title: 'Acc. Days',
      //   dataIndex: 'accrued_days',
      //   key: 'accrued_days',
      //   align: 'right',
      //
      //   sorter: (a: any, b: any) => a.accrued_days - b.accrued_days,
      //   // render: formatters.commas2Digits,
      // },
      {
        title: 'Coupon %',
        dataIndex: 'coupon_rate',
        key: 'coupon_rate',
        align: 'right',

        sorter: (a: any, b: any) => a.coupon_rate - b.coupon_rate,
        render: formatters.commas3Digits,
      },
      {
        title: 'DV01',
        dataIndex: 'bpv',
        key: 'bpv',
        align: 'right',

        sorter: (a: any, b: any) => a.bpv - b.bpv,
        render: formatters.commas2Digits,
      },
      {
        title: '~Duration',
        dataIndex: 'duration',
        key: 'duration',
        align: 'right',

        sorter: (a: any, b: any) => a.duration - b.duration,
        render: formatters.commas2Digits,
      },
      {
        title: 'Yield',
        dataIndex: 'ask_yield',
        key: 'ask_yield',
        align: 'right',

        sorter: (a: any, b: any) => a.ask_yield - b.ask_yield,
        render: (a: number) => formatters.commasExact3Digits(a) + ' %',
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        render: (rating: string) => rating || '-',
      },
    ].filter(
      col =>
        !(
          ['~Duration', 'DV01', 'Coupon %', 'Coupon Date'].includes(
            col.title
          ) && session.scenario_type === HISTORICAL_SCENARIO
        )
    );
  }

  let unrealizedOrMTM = 'Unrealized, ';

  if (
    dataSource[0]?.resourcetype == 'HTMBond' ||
    dataSource[0]?.resourcetype == 'AFSBond'
  ) {
    if (dataSource[0]?.resourcetype == 'HTMBond') {
      unrealizedOrMTM = 'MTM, ';
    }
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        fixed: 'left',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
        render(text: string, record: any) {
          return (
            <Space>
              {session.scenario_type === REAL_TIME_SCENARIO && (
                <Button
                  onClick={() => {
                    setDetailsAssetId(record.underlying_asset);
                    const selectedFin = financialInstruments.filter(fin => {
                      if (fin.id === record.id) return fin;
                    })?.[0];
                    setInstrument(selectedFin);
                    setShowBondDetailsModal(true);
                  }}
                >
                  <ZoomInOutlined />
                </Button>
              )}
              {TextShortener({ text, maxLength: 25 })}
            </Space>
          );
        },
      },
      {
        title: 'Issuer Type',
        dataIndex: 'issuer_type',
        key: 'issuer_type',
        // filters: Object.entries(currencies).map(([id, currency]) => {
        filters: issuerTypeArray.map(type => {
          return {
            text: type,
            value: type,
            // value: parseInt(id),
          };
        }),
        onFilter: (value: string, record: UnderlyingAsset) => {
          return value === record.issuer_type;
        },
        sorter: (a: UnderlyingAsset, b: UnderlyingAsset) => {
          const stringA = a.domicile ?? '';
          const stringB = b.domicile ?? '';
          return stringA.localeCompare(stringB);
        },
        defaultSortOrder: 'ascend',
      },
      {
        title: 'Domicile',
        dataIndex: 'domicile',
        key: 'domicile',
        // filters: Object.entries(currencies).map(([id, currency]) => {
        filters: domicilesArray.map(domicile => {
          return {
            text: domicile,
            value: domicile,
            // value: parseInt(id),
          };
        }),
        onFilter: (value: string, record: UnderlyingAsset) => {
          return value === record.domicile;
        },
        sorter: (a: UnderlyingAsset, b: UnderlyingAsset) => {
          const stringA = a.domicile ?? '';
          const stringB = b.domicile ?? '';
          return stringA.localeCompare(stringB);
        },
        defaultSortOrder: 'ascend',
      },
      {
        title: 'Years To Mat.',
        dataIndex: 'htm_afs_years_to_maturity',
        key: 'htm_afs_years_to_maturity',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.htm_afs_years_to_maturity - b.htm_afs_years_to_maturity,
        render: formatters.commas2Digits,
        defaultSortOrder: 'ascend',
      },
      {
        title: '~Duration',
        dataIndex: 'htm_afs_duration',
        key: 'htm_afs_duration',
        align: 'right',

        sorter: (a: any, b: any) => a.htm_afs_duration - b.htm_afs_duration,
        render: formatters.commasExact3Digits,
      },
      {
        title: 'Coupon %',
        dataIndex: 'coupon',
        key: 'coupon',
        align: 'right',

        sorter: (a: any, b: any) => a.coupon - b.coupon,
        render: formatters.commasExact3Digits,
      },
      {
        title: 'Yield',
        dataIndex: 'ask_yield',
        key: 'ask_yield',
        align: 'right',

        sorter: (a: any, b: any) => a.ask_yield - b.ask_yield,
        render: (a: number) => formatters.commasExact3Digits(a) + ' %',
      },
      {
        title: 'DV01',
        dataIndex: 'htm_afs_dv01',
        key: 'htm_afs_dv01',
        align: 'right',

        sorter: (a: any, b: any) => a.htm_afs_dv01 - b.htm_afs_dv01,
        render: formatters.commas2Digits,
      },
    ];
    additionFields = [
      {
        title: 'Days to coupon',
        dataIndex: 'htm_afs_days_to_coupon',
        key: 'htm_afs_days_to_coupon',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.htm_afs_days_to_coupon - b.htm_afs_days_to_coupon,
        // defaultSortOrder: 'descend',
        render: (value: number) => {
          if (value === 0.1) {
            return '--';
          } else {
            return value;
          }
        },
      },
      // {
      //   title: 'Rating',
      //   dataIndex: 'rating',
      //   render: (rating: string) => rating || '-',
      // },
    ].filter(
      col =>
        !(
          [''].includes(col.title) &&
          session.scenario_type === HISTORICAL_SCENARIO
        )
    );
  }

  if (action) {
    additionFields.push({ title: 'Action', render: action, fixed: 'right' });
  }

  // console.log('dataSource from instrument table: ', dataSource);

  // if (dataSource[0]?.resourcetype === 'Repo') {
  //   firstFields = [
  //     {
  //       title: 'Name',
  //       dataIndex: 'name',
  //       ...columnSearchWidget({
  //         dataIndex: 'name',
  //       }),
  //     },
  //     {
  //       title: 'BBG Ticker',
  //       dataIndex: 'ticker',
  //       key: 'ticker',
  //     },
  //   ];
  // }
  if (dataSource[0]?.resourcetype === 'TBill') {
    firstFields = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      // {
      //   title: 'Region',
      //   dataIndex: 'geography',
      //   key: 'geography',
      //   filters: extractFilters('geography'),
      //   onFilter: (value: any, record: any) => record.geography === value,
      // },
      // {
      //   title: 'BBG Ticker',
      //   dataIndex: 'ticker',
      //   key: 'ticker',
      //
      //   defaultSortOrder: 'descend',
      //   sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      // },
      {
        title: 'Tenor',
        dataIndex: 'tbill_tenor',
        key: 'tbill_tenor',
        render: (value: number) => {
          return auctions_tenors[value];
        },
      },
      // {
      //   title: 'Rate',
      //   dataIndex: 'tbill_rate',
      //   key: 'tbill_rate',
      //   // render: (value: number) => {
      //   //   return auctions_tenors[value];
      //   // },
      // },
    ];
  }

  let columns: ColumnType<any>[];
  if (dataSource[0]?.resourcetype == 'Repo') {
    columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',
      },
      {
        title: 'CCY',
        dataIndex: 'currency',
        key: 'currency',
        filters: currencyFilters,
        onFilter: (value: any, record: ReusableInstrumentTableType) =>
          record.currency === value,
      },
      // {
      //   title: 'BID',
      //   dataIndex: 'bid',
      //   key: 'bid',
      //   align: 'right',
      //
      //   sorter: (a: any, b: any) => a.bid - b.bid,
      //   // render: function Price(val: any, record: ReusableInstrumentTableType) {
      //   //   const price = formatters.priceFormat(
      //   //     val,
      //   //     record.ticker,
      //   //     record.resourcetype
      //   //   );
      //   //
      //   //   if (!priceArrows) return price;
      //   //
      //   //   return <PriceCell priceDiff={record.bid_diff} price={price} />;
      //   // },
      //   render: function RepoBid(
      //     val: any,
      //     record: ReusableInstrumentTableType
      //   ) {
      //     return <div>{val} %</div>;
      //   },
      // },
      {
        title: 'ASK',
        dataIndex: 'ask',
        key: 'ask',
        align: 'right',

        sorter: (a: any, b: any) => a.ask - b.ask,
        // render: function Price(val: any, record: ReusableInstrumentTableType) {
        //   return (
        //     <PriceCell
        //       priceDiff={record.ask_diff}
        //       price={formatters.priceFormat(
        //         val,
        //         record.ticker,
        //         record.resourcetype
        //       )}
        //     />
        //   );
        // },
        render: function RepoAsk(
          val: any,
          record: ReusableInstrumentTableType
        ) {
          return <div>{formatters.commasExact2Digits(val)} %</div>;
        },
      },
    ];

    if (session.scenario_type === REAL_TIME_SCENARIO) {
      columns.push({
        title: 'Maturity',
        dataIndex: 'repo_maturity_date',
        key: 'repo_maturity_date',
        render: (val: string, record: ReusableInstrumentTableType) => {
          return <div>{val.split('T')[0]}</div>;
        },
      });
    }
    columns.push({ title: 'Action', render: action, fixed: 'right' });
  } else if (dataSource[0]?.resourcetype === 'CDS') {
    columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        ...columnSearchWidget({
          dataIndex: 'name',
        }),
      },
      {
        title: 'BBG Ticker',
        dataIndex: 'ticker',
        key: 'ticker',

        defaultSortOrder: 'descend',
        sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      },
      // {
      //   title: 'Open Date',
      //   dataIndex: 'open_date',
      //   key: 'open_date',
      //   // defaultSortOrder: 'descend',
      //   // sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      // },
      {
        title: 'Maturity',
        dataIndex: 'cds_asset_maturity_date',
        key: 'cds_asset_maturity_date',
        // defaultSortOrder: 'descend',
        // sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
        render: (val: string) => {
          return <div>{val.split('T')[0]}</div>;
        },
      },
      {
        title: 'BID',
        dataIndex: 'bid',
        key: 'bid',
        align: 'right',

        sorter: (a: any, b: any) => a.bid - b.bid,
        render: function Price(val: any, record: ReusableInstrumentTableType) {
          const price = formatters.priceFormat(
            val,
            record.ticker,
            record.currency,
            record.resourcetype
          );

          if (!priceArrows) return price;

          return <PriceCell priceDiff={record.bid_diff} price={price} />;
        },
      },
      {
        title: 'ASK',
        dataIndex: 'ask',
        key: 'ask',
        align: 'right',

        sorter: (a: any, b: any) => a.ask - b.ask,
        render: function Price(val: any, record: ReusableInstrumentTableType) {
          return (
            <PriceCell
              priceDiff={record.ask_diff}
              price={formatters.priceFormat(
                val,
                record.ticker,
                record.currency,
                record.resourcetype
              )}
            />
          );
        },
      },
      {
        title: 'CCY',
        dataIndex: 'currency',
        key: 'currency',
        filters: currencyFilters,
        onFilter: (value: any, record: ReusableInstrumentTableType) =>
          record.currency === value,
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',

        sorter: (a: any, b: any) => a.amount - b.amount,
        render: formatters.commasNoDigits,
      },
      {
        title: `${unrealizedOrMTM}` + reportingCurrency.symbol,
        // for bonds this is clean (trading) unrealized PnL, without unrealized interest added
        dataIndex: 'reporting_unrealized_pnl',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.reporting_unrealized_pnl - b.reporting_unrealized_pnl,
        render: formatters.commasNoDigits,
      },
      {
        title: 'Realized, ' + reportingCurrency.symbol,
        dataIndex: 'reporting_realized_pnl',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.reporting_realized_pnl - b.reporting_realized_pnl,
        render: formatters.commasNoDigits,
      },
      // {
      //   title: 'Value, ' + reportingCurrency.symbol,
      //   // for bonds this is clean open value (amount * clean open price)
      //   dataIndex: 'reportingValue',
      //   key: 'reportingValue',
      //   align: 'right',
      //   sorter: (a: any, b: any) => a.reportingValue - b.reportingValue,
      //   render: formatters.commasNoDigits,
      // },
      { title: 'Action', render: action, fixed: 'right' },
    ];
  } else {
    columns = [
      ...firstFields,
      // {
      //   title: 'BBG Ticker',
      //   dataIndex: 'ticker',
      //   key: 'ticker',
      //
      //   defaultSortOrder: 'descend',
      //   sorter: (a: any, b: any) => (a.ticker > b.ticker ? -1 : 1),
      // },
      {
        title: 'BID',
        dataIndex: 'bid',
        key: 'bid',
        align: 'right',

        sorter: (a: any, b: any) => a.bid - b.bid,
        render: function Price(val: any, record: ReusableInstrumentTableType) {
          const price = formatters.priceFormat(
            val,
            record.ticker,
            record.currency,
            record.resourcetype
          );

          if (!priceArrows) return price;

          return <PriceCell priceDiff={record.bid_diff} price={price} />;
        },
      },
      {
        title: 'ASK',
        dataIndex: 'ask',
        key: 'ask',
        align: 'right',

        sorter: (a: any, b: any) => a.ask - b.ask,
        render: function Price(val: any, record: ReusableInstrumentTableType) {
          return (
            <PriceCell
              priceDiff={record.ask_diff}
              price={formatters.priceFormat(
                val,
                record.ticker,
                record.currency,
                record.resourcetype
              )}
            />
          );
        },
      },
      {
        title: 'CCY',
        dataIndex: 'currency',
        key: 'currency',
        filters: currencyFilters,
        onFilter: (value: any, record: ReusableInstrumentTableType) =>
          record.currency === value,
      },
      {
        title: 'Open',
        dataIndex: 'avg_open_price',
        key: 'avg_open_price',
        align: 'right',

        sorter: (a: any, b: any) => a.avg_open_price - b.avg_open_price,
        render: (val: any, record: ReusableInstrumentTableType) =>
          formatters.priceFormat(
            val,
            record.ticker,
            record.currency,
            record.resourcetype
          ),
      },
      // de aici
      {
        // Futures are presented in contracts (amount / lot_size); other asset classes keep raw units.
        // Each table renders a single resourcetype, so the title gate on dataSource[0] is uniform.
        title:
          dataSource[0]?.resourcetype === 'Futures' ? 'Contracts' : 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',

        sorter: (a: any, b: any) => a.amount - b.amount,
        render: (val: any, record: any) =>
          record.resourcetype === 'Futures'
            ? formatters.commasNoDigits(
                formatters.unitsToContracts(val, record.lot_size)
              )
            : formatters.commasNoDigits(val),
      },
      // Futures-only: synthetic-contract expiry countdown (asset-level, identical on every futures row,
      // shown even with no position since it's pre-trade contract info). Gated on the rollover feature
      // being on; separate from the leverage gate (rollover is leverage-independent).
      ...(dataSource[0]?.resourcetype === 'Futures' &&
      session.futures_rollover_periods > 0
        ? [
            {
              title: 'Expiry in, Days',
              key: 'periods_to_next_roll',
              align: 'right',
              // periods remaining × days-per-period (session.frequency) -> days until the contract rolls
              render: () => session.periods_to_next_roll * session.frequency,
            },
          ]
        : []),
      // Futures-only: posted margin per position (raw, in the instrument's own currency).
      // `collateral` is already serialized for every instrument; gated to leveraged futures so no
      // other asset class is affected (each table renders a single resourcetype).
      ...(dataSource[0]?.resourcetype === 'Futures' &&
      session.futures_leverage !== 1
        ? [
            {
              title: 'Posted Margin',
              dataIndex: 'collateral',
              align: 'right',
              sorter: (a: any, b: any) => a.collateral - b.collateral,
              render: formatters.commasNoDigits,
            },
            // Futures-only: how close the position is to a margin call, as a green→red bar of
            // "margin used %" (unrealized loss as a fraction of its loss budget). Filled/red = near
            // liquidation. Reads live `reporting_unrealized_pnl` against the fixed posted margin.
            {
              title: (
                <span>
                  Margin Used{' '}
                  <Popover
                    content={
                      <div style={{ maxWidth: 280 }}>
                        Share of this position&apos;s posted margin that has
                        been eaten by unrealized loss. The position is{' '}
                        <b>auto-liquidated</b> once the loss reaches{' '}
                        {Math.round(
                          (1 - session.futures_maintenance_threshold) * 100
                        )}
                        % of posted margin
                        {session.futures_maintenance_threshold > 0
                          ? ` — i.e. when remaining equity (margin + P&L) falls to ${Math.round(
                              session.futures_maintenance_threshold * 100
                            )}% of the posted margin.`
                          : ' (margin fully wiped).'}
                        <br />
                        <br />
                        0% = full cushion · 100% = at the liquidation point.
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Popover>
                </span>
              ),
              key: 'margin_used',
              width: 130,
              render: (_: unknown, record: any) => {
                // No open position (amount == 0, incl. post-liquidation): keep the bar visible but
                // fully empty with no percentage label, so the column reads as "nothing at risk here".
                if (!record.amount) {
                  // format='' (rather than showInfo={false}) keeps antd's text slot reserved, so this
                  // empty bar's rail is the same length as the labeled bars on active-position rows.
                  return (
                    <div className="margin-used-bar">
                      <Progress percent={0} format={() => ''} />
                    </div>
                  );
                }
                const pct = futuresMarginUsedPct(
                  record.collateral,
                  record.reporting_unrealized_pnl,
                  session.futures_maintenance_threshold
                );
                // Always render at least a small sliver so an unutilized (0%) bar still reads as a
                // (green) bar rather than an empty rail. The label keeps showing the true percent.
                const displayPct = Math.max(pct, 11);
                return (
                  <div className="margin-used-bar">
                    <Progress
                      percent={displayPct}
                      strokeColor={marginUsedColor(pct)}
                      format={() => `${Math.round(pct)}%`}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        title: `${unrealizedOrMTM}` + reportingCurrency.symbol,
        // for bonds this is clean (trading) unrealized PnL, without unrealized interest added
        dataIndex: 'reporting_unrealized_pnl',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.reporting_unrealized_pnl - b.reporting_unrealized_pnl,
        render: formatters.commasNoDigits,
      },
      {
        title: 'Realized, ' + reportingCurrency.symbol,
        dataIndex: 'reporting_realized_pnl',
        align: 'right',

        sorter: (a: any, b: any) =>
          a.reporting_realized_pnl - b.reporting_realized_pnl,
        render: formatters.commasNoDigits,
      },
      {
        title: 'Value, ' + reportingCurrency.symbol,
        // for bonds this is clean open value (amount * clean open price)
        dataIndex: 'reportingValue',
        key: 'reportingValue',
        align: 'right',
        sorter: (a: any, b: any) => a.reportingValue - b.reportingValue,
        render: formatters.commasNoDigits,
      },
      // {
      //   title: 'Return %',
      //   dataIndex: 'return_percent',
      //   key: 'return_percent',
      //   align: 'right',
      //
      //   sorter: (a: any, b: any) => a.return_percent - b.return_percent,
      //   render: function Return(n, record) {
      //     const val = formatters.percent1Digit(n);
      //     return (
      //       <span
      //         style={
      //           record.marketIsOpen
      //             ? { color: n < 0 ? 'red' : 'green' }
      //             : {}
      //         }
      //       >
      //         {val}
      //       </span>
      //     );
      //   },
      // },
      ...additionFields,
    ];
  }

  return (
    <div>
      <Table
        rowKey="id"
        bordered
        dataSource={dataSource}
        pagination={false}
        //TODO: vertical scrolling so header sticks scroll={{ x: 3000, y: 300 }}
        {...({
          rowClassName: (record: any) => {
            if (marketHours && !record.marketIsOpen) return 'disabled-row';
            if (record.id === selectedInstrumentId) return 'selected-row';
          },
        } as any)}
        columns={columns}
      />
      {showDetailsModal && (
        <ETFDetailsModal
          assetId={detailsAssetId}
          visible={showDetailsModal}
          hide={() => setShowDetailsModal(false)}
        />
      )}
      {showBondDetailsModal && (
        <BondDetailsModal
          assetId={detailsAssetId}
          visible={showBondDetailsModal}
          instrument={instrument}
          hide={() => setShowBondDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default InstrumentTable;
